import type { Chord, NoteEvent } from '@tunasong/music-lib'
import { chordToEvents } from '../player/chord-to-events.js'
import { useStabScheduler } from '../player/stab-scheduler.js'

const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM
const QUARTER_NOTE_MS = BEAT_MS
const HALF_NOTE_MS = BEAT_MS * 2

export interface ChordStab {
  events: NoteEvent[]
  delayMs: number
  durationMs: number
}

export interface BuildAuditionStabsInput {
  prefix: Chord[]
  endingChord: Chord | null
  penultSubstitution: Chord | null
}

/**
 * Build the sequence of chord stabs for one cadence audition.
 *  - Prefix chords play sequentially at 100 BPM (one quarter-note each, 600 ms).
 *  - Ending chord plays as a half-note (1200 ms) so the ear hears it land.
 *  - When `penultSubstitution` is non-null, the prefix's LAST chord is replaced
 *    by it before the events are computed (used by the plagal card to swap V→IV).
 *  - When `endingChord` is null (suppressed cadence), returns empty array.
 */
export const buildAuditionStabs = (input: BuildAuditionStabsInput): ChordStab[] => {
  const { prefix, endingChord, penultSubstitution } = input
  if (!endingChord) {
    return []
  }
  const effectivePrefix = penultSubstitution ? [...prefix.slice(0, -1), penultSubstitution] : prefix
  const stabs: ChordStab[] = []
  let cursor = 0
  for (const chord of effectivePrefix) {
    stabs.push({
      events: chordToEvents(chord, '4n'),
      delayMs: cursor,
      durationMs: QUARTER_NOTE_MS,
    })
    cursor += QUARTER_NOTE_MS
  }
  stabs.push({
    events: chordToEvents(endingChord, '2n'),
    delayMs: cursor,
    durationMs: HALF_NOTE_MS,
  })
  return stabs
}

export interface UseCadenceLabPlaybackProps extends BuildAuditionStabsInput {}

/**
 * One-shot cadence audition playback. Thin wrapper over `useStabScheduler` —
 * see `frontend/music-ui/src/player/stab-scheduler.ts` for scheduling semantics.
 */
export const useCadenceLabPlayback = (props: UseCadenceLabPlaybackProps) => {
  const { play, stop } = useStabScheduler({
    buildStabs: () => buildAuditionStabs(props),
  })
  return { play, stop }
}
